import React, { useEffect, useMemo, useState } from 'react'
import {
  Modal,
  Button,
  Form,
  Input,
  Typography,
  Space,
  Card,
  List,
  Tag,
  Spin,
  Empty,
  Tabs,
  Badge,
  Tooltip,
  Divider,
  Row,
  Col,
  Statistic,
} from 'antd'
import { FaCoins, FaGift, FaTasks, FaStore } from 'react-icons/fa'
import { toast } from 'react-toastify'
import {
  getQuest,
  followShop,
  getUserCoins,
  getReward,
  getVoucherWithCoin,
  claimVoucherWithCoin,
} from '../../../api/saveVoucher'

const { Text, Title } = Typography

const GetVoucherWithCoin = ({ visible, onClose }) => {
  const [cookieValue, setCookieValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isClaiming, setIsClaiming] = useState('')
  const [isFollowing, setIsFollowing] = useState('')
  const [isGettingReward, setIsGettingReward] = useState('')

  // Data states
  const [userCoins, setUserCoins] = useState(null)
  const [questList, setQuestList] = useState([])
  const [coinVouchers, setCoinVouchers] = useState([])

  const isCookieValid = useMemo(() => cookieValue && cookieValue.trim().length >= 10, [cookieValue])
  const isUserIdValid = useMemo(() => userCoins?.coins?.user_id, [userCoins])

  const loadData = async () => {
    if (!isCookieValid) return

    try {
      setIsLoading(true)

      // Đầu tiên lấy thông tin coin để có user_id
      const coins = await getUserCoins(cookieValue.trim())
      setUserCoins(coins)

      if (coins?.coins?.user_id) {
        // Sau khi có user_id, lấy quest và voucher
        const [quests, vouchers] = await Promise.all([
          getQuest(cookieValue.trim(), coins.coins.user_id),
          getVoucherWithCoin(cookieValue.trim()),
        ])

        setQuestList(Array.isArray(quests?.data?.task_users) ? quests.data.task_users : [])
        setCoinVouchers(Array.isArray(vouchers?.data?.benefits) ? vouchers.data.benefits : [])
      }
    } catch (e) {
      toast.error('Không thể tải dữ liệu')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (visible && isCookieValid) {
      loadData()
    }
  }, [visible, isCookieValid])

  const handleFollowShop = async (shopId, taskId) => {
    if (!isCookieValid || !isUserIdValid) {
      toast.warn('Vui lòng dán cookie Shopee hợp lệ')
      return
    }
    try {
      setIsFollowing(String(shopId))
      const result = await followShop(cookieValue.trim(), shopId)
      if (result && result.error === 0) {
        toast.success('Đã follow shop thành công!')

        // Chờ 1 giây trước khi claim reward
        await new Promise((resolve) => setTimeout(resolve, 1000))

        // Tự động claim reward sau khi follow shop
        try {
          setIsGettingReward(String(taskId))
          const rewardResult = await getReward(cookieValue.trim(), taskId, userCoins.coins.user_id)
          if (rewardResult && rewardResult.code === 0) {
            toast.success('Đã tự động nhận phần thưởng!')
          } else {
            const rewardMsg = rewardResult?.msg || 'Không thể tự động nhận phần thưởng'
            toast.info(rewardMsg)
          }
        } catch (rewardError) {
          toast.info('Không thể tự động nhận phần thưởng, vui lòng thử lại sau')
        } finally {
          setIsGettingReward('')
        }

        // Reload quest data to update status
        await loadData()
      } else {
        const msg = result?.error_msg || 'Không thể follow shop. Vui lòng thử lại.'
        toast.error(msg)
      }
    } catch (e) {
      toast.error('Có lỗi xảy ra khi follow shop')
    } finally {
      setIsFollowing('')
    }
  }

  const handleGetReward = async (taskId) => {
    if (!isCookieValid || !isUserIdValid) {
      toast.warn('Vui lòng dán cookie Shopee hợp lệ')
      return
    }
    try {
      setIsGettingReward(String(taskId))
      const result = await getReward(cookieValue.trim(), taskId, userCoins.coins.user_id)
      if (result && result.code === 0) {
        toast.success('Đã nhận phần thưởng thành công!')
        // Reload quest data to update status
        await loadData()
      } else {
        const msg = result?.msg || 'Không thể nhận phần thưởng. Vui lòng thử lại.'
        toast.error(msg)
      }
    } catch (e) {
      toast.error('Có lỗi xảy ra khi nhận phần thưởng')
    } finally {
      setIsGettingReward('')
    }
  }

  const handleClaimVoucher = async (benefitId, voucherCode) => {
    if (!isCookieValid || !isUserIdValid) {
      toast.warn('Vui lòng dán cookie Shopee hợp lệ')
      return
    }
    try {
      setIsClaiming(String(benefitId))
      const result = await claimVoucherWithCoin(cookieValue.trim(), benefitId, voucherCode)
      if (result && result.code === 0) {
        toast.success('Đã claim voucher thành công!')
        // Reload voucher data
        await loadData()
      } else {
        let errorMsg = 'Không thể claim voucher. Vui lòng thử lại.'

        // Xử lý các lỗi cụ thể
        if (result?.code === 20018) {
          errorMsg = 'Lỗi giảm coin: Không đủ coin hoặc có vấn đề với tài khoản'
        } else if (result?.msg) {
          errorMsg = result.msg
        }

        toast.error(errorMsg)
      }
    } catch (e) {
      toast.error('Có lỗi xảy ra khi claim voucher')
    } finally {
      setIsClaiming('')
    }
  }

  const renderCompletedQuests = () => {
    const completedQuests = questList.filter((q) => q.task_finish_status !== 0)

    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!completedQuests || completedQuests.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có nhiệm vụ đã hoàn thành' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={completedQuests}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[
                item.redeemable ? (
                  <Tooltip title='Nhận phần thưởng'>
                    <Button
                      type='primary'
                      size='small'
                      className='bg-green-600'
                      loading={isGettingReward === String(item.task.id)}
                      onClick={() => handleGetReward(item.task.id)}
                      disabled={!isCookieValid || !isUserIdValid}
                    >
                      Nhận thưởng
                    </Button>
                  </Tooltip>
                ) : (
                  <Tag color='green'>Đã nhận thưởng</Tag>
                ),
              ]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.task.task_name}
                    </Text>
                    <Tag color={item.redeemable ? 'green' : 'blue'}>
                      {item.redeemable ? 'Có thể nhận thưởng' : 'Đã nhận thưởng'}
                    </Tag>
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Phần thưởng: {item.task.reward_amount} coin</div>
                    <div>Shop: {item.task.assets_config?.shop_username || 'N/A'}</div>
                    <div>Trạng thái: {item.redeemable ? 'Chưa nhận thưởng' : 'Đã nhận thưởng'}</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const renderIncompleteWithReward = () => {
    const incompleteWithReward = questList.filter(
      (q) => q.task_finish_status === 0 && q.task.reward_stock > 0
    )

    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!incompleteWithReward || incompleteWithReward.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có nhiệm vụ chưa hoàn thành còn phần thưởng' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={incompleteWithReward}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[
                <Tooltip
                  title={
                    !isUserIdValid
                      ? 'Đang tải thông tin tài khoản...'
                      : 'Follow shop để hoàn thành nhiệm vụ'
                  }
                >
                  <Button
                    type='default'
                    size='small'
                    className='border-blue-500 text-blue-600'
                    loading={isFollowing === String(item.task.assets_config?.shop_id)}
                    onClick={() => handleFollowShop(item.task.assets_config?.shop_id, item.task.id)}
                    disabled={!isCookieValid || !isUserIdValid}
                  >
                    Follow Shop
                  </Button>
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.task.task_name}
                    </Text>
                    <Tag color='orange'>Còn phần thưởng</Tag>
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Phần thưởng: {item.task.reward_amount} coin</div>
                    <div>Shop: {item.task.assets_config?.shop_username || 'N/A'}</div>
                    <div>Trạng thái: Cần follow shop để hoàn thành</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const renderCoinVouchers = () => {
    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!coinVouchers || coinVouchers.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có voucher nào' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={coinVouchers}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[
                <Tooltip title='Sao chép mã'>
                  <Button
                    size='small'
                    className='hover:!border-gray-300'
                    onClick={() =>
                      navigator.clipboard
                        .writeText(item.voucher_code)
                        .then(() => toast.success('Đã sao chép mã'))
                    }
                  >
                    Sao chép mã
                  </Button>
                </Tooltip>,
                <Tooltip
                  title={
                    !isCookieValid
                      ? 'Dán cookie trước khi claim'
                      : !isUserIdValid
                      ? 'Đang tải thông tin tài khoản...'
                      : 'Claim voucher này'
                  }
                >
                  <Button
                    type='primary'
                    size='small'
                    className='bg-gradient-to-r from-yellow-600 to-orange-600 shadow hover:from-yellow-700 hover:to-orange-700'
                    loading={isClaiming === String(item.benefit_id)}
                    onClick={() => handleClaimVoucher(item.benefit_id, item.voucher_code)}
                    disabled={!isCookieValid || !isUserIdValid}
                  >
                    Claim
                  </Button>
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.title || 'Voucher'}
                    </Text>
                    <Tag color='gold' className='font-mono'>
                      {item.voucher_code}
                    </Tag>
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Giá: {item.coin_cost} coin</div>
                    <div>Mô tả: {item.description || 'N/A'}</div>
                    <div>Trạng thái: {item.is_redeemed ? 'Đã nhận quà' : 'Chưa nhận quà'}</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const renderIncompleteNoReward = () => {
    const incompleteNoReward = questList.filter(
      (q) => q.task_finish_status === 0 && q.task.reward_stock === 0
    )

    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!incompleteNoReward || incompleteNoReward.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có nhiệm vụ chưa hoàn thành hết phần thưởng' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={incompleteNoReward}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[<Tag color='red'>Hết phần thưởng</Tag>]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.task.task_name}
                    </Text>
                    <Tag color='red'>Hết phần thưởng</Tag>
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Phần thưởng: {item.task.reward_amount} coin</div>
                    <div>Shop: {item.task.assets_config?.shop_username || 'N/A'}</div>
                    <div>Trạng thái: Hết phần thưởng, không thể hoàn thành</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const renderCompletedNoReward = () => {
    const completedNoReward = questList.filter(
      (q) => q.task_finish_status !== 0 && q.redeemable === false
    )

    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!completedNoReward || completedNoReward.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có nhiệm vụ đã hoàn thành chưa nhận thưởng' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={completedNoReward}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[
                item.task.reward_stock > 0 ? (
                  <Tooltip title='Nhận phần thưởng'>
                    <Button
                      type='primary'
                      size='small'
                      className='bg-green-600'
                      loading={isGettingReward === String(item.task.id)}
                      onClick={() => handleGetReward(item.task.id)}
                      disabled={!isCookieValid || !isUserIdValid}
                    >
                      Nhận thưởng
                    </Button>
                  </Tooltip>
                ) : (
                  <Tag color='red'>Hết quà</Tag>
                ),
              ]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.task.task_name}
                    </Text>
                    {item.task.reward_stock > 0 ? (
                      <Tag color='green'>Chưa nhận thưởng</Tag>
                    ) : (
                      <Tag color='red'>Hết quà</Tag>
                    )}
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Phần thưởng: {item.task.reward_amount} coin</div>
                    <div>Shop: {item.task.assets_config?.shop_username || 'N/A'}</div>
                    <div>
                      Trạng thái: {item.task.reward_stock > 0 ? 'Có thể nhận thưởng' : 'Hết quà'}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const renderCompletedWithReward = () => {
    const completedWithReward = questList.filter(
      (q) => q.task_finish_status !== 0 && q.redeemable === true
    )

    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!completedWithReward || completedWithReward.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có nhiệm vụ đã hoàn thành đã nhận thưởng' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={completedWithReward}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[<Tag color='green'>Đã nhận thưởng</Tag>]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.task.task_name}
                    </Text>
                    <Tag color='blue'>Đã nhận thưởng</Tag>
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Phần thưởng: {item.task.reward_amount} coin</div>
                    <div>Shop: {item.task.assets_config?.shop_username || 'N/A'}</div>
                    <div>Trạng thái: Đã nhận thưởng</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const renderVoucherNotRedeemed = () => {
    const voucherNotRedeemed = coinVouchers.filter((v) => !v.is_redeemed)

    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!voucherNotRedeemed || voucherNotRedeemed.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có voucher nào chưa nhận quà' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={voucherNotRedeemed}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[
                <Tooltip title='Sao chép mã'>
                  <Button
                    size='small'
                    className='hover:!border-gray-300'
                    onClick={() =>
                      navigator.clipboard
                        .writeText(item.voucher_code)
                        .then(() => toast.success('Đã sao chép mã'))
                    }
                  >
                    Sao chép mã
                  </Button>
                </Tooltip>,
                <Tooltip
                  title={
                    !isCookieValid
                      ? 'Dán cookie trước khi claim'
                      : !isUserIdValid
                      ? 'Đang tải thông tin tài khoản...'
                      : 'Claim voucher này'
                  }
                >
                  <Button
                    type='primary'
                    size='small'
                    className='bg-gradient-to-r from-yellow-600 to-orange-600 shadow hover:from-yellow-700 hover:to-orange-700'
                    loading={isClaiming === String(item.benefit_id)}
                    onClick={() => handleClaimVoucher(item.benefit_id, item.voucher_code)}
                    disabled={!isCookieValid || !isUserIdValid}
                  >
                    Claim
                  </Button>
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.benefit_name || 'Voucher'}
                    </Text>
                    <Tag color='gold' className='font-mono'>
                      {item.voucher_code}
                    </Tag>
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Giá: {item.coins_cost} coin</div>
                    <div>Điều kiện: {item.usage_condition || 'N/A'}</div>
                    <div>
                      Giảm tối đa:{' '}
                      {item.discount_cap ? `${(item.discount_cap / 1000).toFixed(0)}K` : 'N/A'}
                    </div>
                    <div>
                      Đơn tối thiểu:{' '}
                      {item.min_spend ? `${(item.min_spend / 1000).toFixed(0)}K` : 'N/A'}
                    </div>
                    <div>
                      Hạn sử dụng:{' '}
                      {item.voucher_valid_till
                        ? new Date(item.voucher_valid_till * 1000).toLocaleDateString('vi-VN')
                        : 'N/A'}
                    </div>
                    <div>Loại: {item.ui_icon_text || 'N/A'}</div>
                    <div>Trạng thái: Chưa nhận quà</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const renderVoucherRedeemed = () => {
    const voucherRedeemed = coinVouchers.filter((v) => v.is_redeemed)

    if (isLoading)
      return (
        <div className='w-full flex justify-center py-8'>
          <Spin size='large' />
        </div>
      )
    if (!voucherRedeemed || voucherRedeemed.length === 0)
      return (
        <div className='py-6'>
          <Empty description='Không có voucher nào đã nhận quà' />
        </div>
      )

    return (
      <div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400'>
        <List
          itemLayout='horizontal'
          dataSource={voucherRedeemed}
          className='grid gap-3'
          renderItem={(item) => (
            <List.Item
              className='bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-4'
              actions={[
                <Tooltip title='Sao chép mã'>
                  <Button
                    size='small'
                    className='hover:!border-gray-300'
                    onClick={() =>
                      navigator.clipboard
                        .writeText(item.voucher_code)
                        .then(() => toast.success('Đã sao chép mã'))
                    }
                  >
                    Sao chép mã
                  </Button>
                </Tooltip>,
                <Tag color='green'>Đã nhận quà</Tag>,
              ]}
            >
              <List.Item.Meta
                title={
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-gray-800'>
                      {item.benefit_name || 'Voucher'}
                    </Text>
                    <Tag color='gold' className='font-mono'>
                      {item.voucher_code}
                    </Tag>
                  </div>
                }
                description={
                  <div className='text-xs text-gray-500 space-y-1'>
                    <div>Giá: {item.coins_cost} coin</div>
                    <div>Điều kiện: {item.usage_condition || 'N/A'}</div>
                    <div>
                      Giảm tối đa:{' '}
                      {item.discount_cap ? `${(item.discount_cap / 1000).toFixed(0)}K` : 'N/A'}
                    </div>
                    <div>
                      Đơn tối thiểu:{' '}
                      {item.min_spend ? `${(item.min_spend / 1000).toFixed(0)}K` : 'N/A'}
                    </div>
                    <div>
                      Hạn sử dụng:{' '}
                      {item.voucher_valid_till
                        ? new Date(item.voucher_valid_till * 1000).toLocaleDateString('vi-VN')
                        : 'N/A'}
                    </div>
                    <div>Loại: {item.ui_icon_text || 'N/A'}</div>
                    <div>Trạng thái: Đã nhận quà</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    )
  }

  const questTab = {
    key: 'quest',
    label: (
      <div className='flex items-center gap-2'>
        <FaTasks className='text-blue-500' />
        <span>Nhiệm vụ</span>
        <Badge count={questList.length} overflowCount={999} className='ml-1' />
      </div>
    ),
    children: (
      <Tabs
        defaultActiveKey='incomplete-with-reward'
        items={[
          {
            key: 'incomplete-with-reward',
            label: (
              <div className='flex items-center gap-2'>
                <FaTasks className='text-orange-500' />
                <span>Còn phần thưởng</span>
                <Badge
                  count={
                    questList.filter((q) => q.task_finish_status === 0 && q.task.reward_stock > 0)
                      .length
                  }
                  overflowCount={999}
                  className='ml-1'
                />
              </div>
            ),
            children: <div className='pt-2'>{renderIncompleteWithReward()}</div>,
          },
          {
            key: 'incomplete-no-reward',
            label: (
              <div className='flex items-center gap-2'>
                <FaTasks className='text-red-500' />
                <span>Hết phần thưởng</span>
                <Badge
                  count={
                    questList.filter((q) => q.task_finish_status === 0 && q.task.reward_stock === 0)
                      .length
                  }
                  overflowCount={999}
                  className='ml-1'
                />
              </div>
            ),
            children: <div className='pt-2'>{renderIncompleteNoReward()}</div>,
          },
          {
            key: 'completed-no-reward',
            label: (
              <div className='flex items-center gap-2'>
                <FaTasks className='text-green-500' />
                <span>Chưa nhận thưởng</span>
                <Badge
                  count={
                    questList.filter((q) => q.task_finish_status !== 0 && q.redeemable === false)
                      .length
                  }
                  overflowCount={999}
                  className='ml-1'
                />
              </div>
            ),
            children: <div className='pt-2'>{renderCompletedNoReward()}</div>,
          },
          {
            key: 'completed-with-reward',
            label: (
              <div className='flex items-center gap-2'>
                <FaTasks className='text-blue-500' />
                <span>Đã nhận thưởng</span>
                <Badge
                  count={
                    questList.filter((q) => q.task_finish_status !== 0 && q.redeemable === true)
                      .length
                  }
                  overflowCount={999}
                  className='ml-1'
                />
              </div>
            ),
            children: <div className='pt-2'>{renderCompletedWithReward()}</div>,
          },
        ]}
      />
    ),
  }

  const voucherTab = {
    key: 'voucher',
    label: (
      <div className='flex items-center gap-2'>
        <FaGift className='text-yellow-500' />
        <span>Voucher bằng Coin</span>
        <Badge count={coinVouchers.length} overflowCount={999} className='ml-1' />
      </div>
    ),
    children: (
      <Tabs
        defaultActiveKey='voucher-not-redeemed'
        items={[
          {
            key: 'voucher-not-redeemed',
            label: (
              <div className='flex items-center gap-2'>
                <FaGift className='text-yellow-500' />
                <span>Chưa nhận quà</span>
                <Badge
                  count={coinVouchers.filter((v) => !v.is_redeemed).length}
                  overflowCount={999}
                  className='ml-1'
                />
              </div>
            ),
            children: <div className='pt-2'>{renderVoucherNotRedeemed()}</div>,
          },
          {
            key: 'voucher-redeemed',
            label: (
              <div className='flex items-center gap-2'>
                <FaGift className='text-green-500' />
                <span>Đã nhận quà</span>
                <Badge
                  count={coinVouchers.filter((v) => v.is_redeemed).length}
                  overflowCount={999}
                  className='ml-1'
                />
              </div>
            ),
            children: <div className='pt-2'>{renderVoucherRedeemed()}</div>,
          },
        ]}
      />
    ),
  }

  return (
    <Modal
      title={
        <div className='flex items-center gap-2'>
          <div className='p-2 rounded-lg bg-gradient-to-br from-yellow-100 to-orange-100'>
            <FaCoins className='text-yellow-600 text-xl' />
          </div>
          <span className='text-lg font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent'>
            Voucher & Nhiệm vụ bằng Coin
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      className='voucher-coin-modal'
    >
      <div className='bg-gradient-to-br from-yellow-50 via-white to-orange-50 p-4 rounded-2xl'>
        <Space direction='vertical' size='large' className='w-full'>
          {/* User Info & Cookie Input */}
          <Card
            className='rounded-xl border-none shadow-sm bg-white/70 backdrop-blur'
            bodyStyle={{ padding: 16 }}
          >
            <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-3'>
              <div className='text-gray-700'>
                <Text>
                  Dán cookie Shopee để tự động lấy thông tin tài khoản và thực hiện nhiệm vụ, claim
                  voucher bằng coin.
                </Text>
              </div>
              <div className='flex gap-2'>
                <Tooltip
                  title={
                    !isCookieValid
                      ? 'Dán cookie trước khi làm mới'
                      : 'Làm mới dữ liệu quest và voucher'
                  }
                >
                  <Button
                    onClick={loadData}
                    disabled={isLoading || !isCookieValid}
                    className='hover:border-gray-300'
                  >
                    Làm mới dữ liệu
                  </Button>
                </Tooltip>
              </div>
            </div>

            <Divider className='my-3' />

            <Form.Item label='Cookie Shopee' required className='mb-0'>
              <Input.TextArea
                rows={4}
                value={cookieValue}
                onChange={(e) => setCookieValue(e.target.value)}
                placeholder='Dán cookie Shopee vào đây...'
                className='font-mono'
              />
            </Form.Item>

            {/* User Coins Display */}
            {userCoins && (
              <div className='mt-4 p-3 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg'>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title='Coin hiện có'
                      value={userCoins.coins?.available_amount || 0}
                      suffix='coin'
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title='User ID (Tự động)'
                      value={userCoins.coins?.user_id || 'Đang tải...'}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </Card>

          {/* Tabs for Quest and Vouchers */}
          <Card
            className='rounded-xl border-none shadow-md bg-white/80 backdrop-blur'
            bodyStyle={{ padding: 16 }}
          >
            <Tabs defaultActiveKey='quest' items={[questTab, voucherTab]} />
          </Card>
        </Space>
      </div>
    </Modal>
  )
}

export default GetVoucherWithCoin
